import React, { useEffect, useMemo } from "react";
import { Box, Dialog, Grid, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { History } from "lucide-react";
import CustomTable from "../../common/CustomTable";
import { Column } from "../../../redux/slice/commonSlice";
import { getProductPurchasePricingHistory, useProductReducer } from "../../../redux/slice/product/createProductSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import { useModalStyles } from "../../common/useModalStyles";

interface ProductCogsHistoryPopupProps {
  open: boolean;
  onClose: () => void;
  productId: number;
  hasSKUPrice: boolean;
}

const ProductCogsHistoryPopup: React.FC<ProductCogsHistoryPopupProps> = ({
  open,
  onClose,
  productId,
  hasSKUPrice,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    bodySx,
    footerSx,
    cancelBtnSx,
  } = useModalStyles();
  const dispatch = useAppDispatch();
  const { productPurchasePricingHistory } = useProductReducer();

  const columns: Column[] = useMemo(() => {
    const cols: Column[] = [
      { id: "effectiveDate", label: "Effective Date", minWidth: 100 },
      { id: "inactiveDate", label: "Inactive Date", minWidth: 100 },
    ];
    if (hasSKUPrice) {
      cols.push({ id: "sku", label: "SKU", minWidth: 120 });
    }
    cols.push(
      { id: "purchasePrice", label: "Purchase Price", minWidth: 100, align: "right" },
      { id: "auditTimestamp", label: "Last Updated Time", minWidth: 120 },
    );
    return cols;
  }, [hasSKUPrice]);

  useEffect(() => {
    if (open && productId > 0) {
      dispatch(getProductPurchasePricingHistory({ productId }));
    }
  }, [open, productId, dispatch]);

  const rows = useMemo(() => {
    return (productPurchasePricingHistory || []).map((obj: any) => ({
      ...obj,
      sku: obj?.sku || obj?.productItem?.sku || "-",
      purchasePrice: <CustomCurrencyField externalValue={obj?.purchasePrice} displayType="text" />,
    }));
  }, [productPurchasePricingHistory]);

  return (
    <Dialog
      open={open}
      keepMounted
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{ sx: paperSx(isMobile) }}
      onClick={(event) => event.stopPropagation()}
      aria-describedby="product-cogs-history-dialog-description"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <History size={20} color={tokens.accent} strokeWidth={1.8} />
          </Box>
          <Box sx={{ flex: 1, pr: 4 }}>
            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
              COGs History
            </Typography>
            <Typography id="product-cogs-history-dialog-description" sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
              View purchase price changes over time.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            ...bodySx,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarGutter: "stable",
            "&::-webkit-scrollbar": { width: 6, height: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
              borderRadius: 4,
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CustomTable
                columns={columns}
                rows={rows}
                disablePagination
                borderBox
                emptyText="No COGs history found"
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={onClose}
              sx={{
                ...cancelBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "1px solid",
                cursor: "pointer",
                order: { xs: 2, sm: 1 },
              }}
            >
              Cancel
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ProductCogsHistoryPopup;
